import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <section id="categories" className="bg-background-50">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("category.title")}
            </h2>
            <p className="mt-2 max-w-xl text-base text-foreground-600">
              {t("category.subtitle")}
            </p>
          </div>

          <div className="relative" ref={ref}>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex w-full items-center justify-between gap-3 rounded-lg border border-foreground-200 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 whitespace-nowrap cursor-pointer md:w-64"
            >
              <span>{t("category.dropdownLabel")}</span>
              <span className="w-4 h-4 flex items-center justify-center">
                <i
                  className={`ri-arrow-down-s-line transition-transform ${
                    open ? "rotate-180" : ""
                  }`}
                ></i>
              </span>
            </button>

            {open && (
              <div className="absolute right-0 left-0 z-20 mt-2 max-h-80 overflow-y-auto rounded-lg border border-foreground-200 bg-background-50 py-1">
                {categories.map((cat) => (
                  <a
                    key={cat.key}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 whitespace-nowrap cursor-pointer"
                  >
                    <span>{t(`category.${cat.key}`)}</span>
                    <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                      <i className="ri-arrow-right-up-line"></i>
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {categories.map((cat) => (
            <a
              key={cat.key}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 cursor-pointer"
            >
              <div className="relative h-40 w-full overflow-hidden">
                <img
                  src={cat.image}
                  alt={t(`category.${cat.key}`)}
                  title={`${t(`category.${cat.key}`)}`}
                  className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                />
                <span className="absolute left-2.5 top-2.5 inline-flex items-center rounded-full bg-background-50/90 px-2.5 py-0.5 text-xs font-semibold text-foreground-800">
                  {t(`category.${cat.tagKey}`)}
                </span>
              </div>
              <div className="flex flex-1 flex-col justify-between gap-2 p-3">
                <span className="text-sm font-medium text-foreground-900 whitespace-nowrap overflow-hidden text-ellipsis">
                  {t(`category.${cat.key}`)}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-700">
                  {t("category.view")}
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </span>
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}